import { useEffect, useState, memo } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

import { ZoneDTO } from "../../DTOs/ZoneDTO";
import { getContrastTextColor } from "../../utilities/helpers/getContrastTextColor";
import useDebounce from "../../components/Hooks/useDebounce";
import { FilterBar, FilterCheckbox, FilterField } from "../../components/FilterBar/FilterBar";

type StoppagesTableHeaderProps = {
  selectedStatus: string;
  setSelectedStatus: React.Dispatch<React.SetStateAction<string>>;
  selectedJobType: { value: number; label: string } | null;
  setSelectedJobType: React.Dispatch<React.SetStateAction<{ value: number; label: string } | null>>;
  machineFilter: string;
  onMachineFilterChange: (value: string) => void;
  selectedZone: ZoneDTO | null;
  setSelectedZone: React.Dispatch<React.SetStateAction<ZoneDTO | null>>;
  jobTypeOptions: { value: number; label: string }[];
  isJobTypeOptionsLoading: boolean;
  zoneOptions: ZoneDTO[];
  isZoneOptionsLoading: boolean;
  unverifiedOnly: boolean;
  setUnverifiedOnly: React.Dispatch<React.SetStateAction<boolean>>;
  showNeedsChecking: boolean;
}

const STOPPAGE_STATUS_OPTIONS = ["Down & Up", "Down"];

const STATUS_LABEL_KEYS: Record<string, "filters.downAndUp" | "filters.down"> = { "Down & Up": "filters.downAndUp", "Down": "filters.down" };

const StoppagesTableHeader: React.FC<StoppagesTableHeaderProps> = memo(({
  selectedStatus,
  setSelectedStatus,
  selectedJobType,
  setSelectedJobType,
  machineFilter,
  onMachineFilterChange,
  selectedZone,
  setSelectedZone,
  jobTypeOptions,
  isJobTypeOptionsLoading,
  zoneOptions,
  isZoneOptionsLoading,
  unverifiedOnly,
  setUnverifiedOnly,
  showNeedsChecking
}) => {
  const { t } = useTranslation("stoppages");
  const [machineInput, setMachineInput] = useState(machineFilter);
  const [prevMachineFilter, setPrevMachineFilter] = useState(machineFilter);
  const debouncedMachineInput = useDebounce(machineInput, 400);

  if (machineFilter !== prevMachineFilter) { // Note: keeps the box in step when the filter is seeded from a link rather than typed
    setPrevMachineFilter(machineFilter);
    setMachineInput(machineFilter);
  }

  useEffect(() => {
    onMachineFilterChange(debouncedMachineInput);
  }, [debouncedMachineInput, onMachineFilterChange]);

  return (
    <FilterBar>
      <FilterField label={t("fields.status")}>
        <Autocomplete
          classes={{ root: "w-full md:min-w-[12rem]" }}
          size="small"
          value={selectedStatus}
          options={STOPPAGE_STATUS_OPTIONS}
          getOptionLabel={(option) => t(STATUS_LABEL_KEYS[option])}
          onChange={(_, val) => {
            if (!val) return;
            else setSelectedStatus(val);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("fields.status")}
            />
          )}
          renderOption={(props, option) =>
            <li
              {...props}
              key={option}
            >{t(STATUS_LABEL_KEYS[option])}</li>}
        />
      </FilterField>

      <FilterField label={t("filters.jobType")}>
        <Autocomplete
          classes={{ root: 'w-full md:min-w-[12rem]' }}
          size="small"
          value={selectedJobType}
          options={jobTypeOptions}
          loading={isJobTypeOptionsLoading}
          onChange={(_, val) => setSelectedJobType(val)}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("filters.jobType")}
            />
          )}
          renderOption={(props, option) =>
            <li
              {...props}
              key={option.value}
            >{option.label}</li>}
        />
      </FilterField>

      <FilterField label={t("fields.machine")}>
        <TextField
          size="small"
          label={t("fields.machine")}
          value={machineInput}
          onChange={(e) => setMachineInput(e.target.value)}
          className="md:min-w-[12rem] w-full"
          slotProps={{
            input: {
              endAdornment:
                <FontAwesomeIcon
                  className={`transition ease-out ${machineInput.length > 0 ? 'opacity-100 cursor-pointer' : 'opacity-0'} text-theme-text`}
                  icon={faXmark}
                  onClick={() => setMachineInput("")}
                />
            }
          }}
        />
      </FilterField>

      <FilterField label={t("fields.zone")}>
        <Autocomplete
          classes={{ root: "w-full md:min-w-[12rem]" }}
          size="small"
          value={selectedZone}
          options={zoneOptions}
          loading={isZoneOptionsLoading}
          onChange={(_, val) => setSelectedZone(val)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option.zone ?? ""}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("fields.zone")}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  endAdornment: (
                    <>
                      {isZoneOptionsLoading && <FontAwesomeIcon icon={faSpinner} spin />}
                      {params.slotProps.input.endAdornment}
                    </>
                  )
                }
              }}
            />
          )}
          renderOption={(props, option) => <li
            {...props}
            key={option.id}
            style={{
              backgroundColor: option.hexColour,
              color: getContrastTextColor(option.hexColour),
              fontWeight: "bold"
            }}
          >{option.zone}</li>}
        />
      </FilterField>

      {showNeedsChecking && <FilterCheckbox
        label={t("filters.needsChecking")}
        checked={unverifiedOnly}
        onChange={setUnverifiedOnly}
        title={t("filters.needsCheckingTitle")}
      />}
    </FilterBar>
  );
});

export default StoppagesTableHeader;
